import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Search,
  Loader2,
  AlertCircle,
  Package,
  Factory,
  Receipt,
} from 'lucide-react';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { formatDate } from '../utils/dateUtils';
import {
  trackingApiService,
  InvoiceTrackingDto,
  InvoiceTrackingLine,
  InvoiceTrackingMaterialIssue,
  InvoiceTrackingWorkOrder,
} from '../services/trackingApi';

type MaterialIssueWithWo = InvoiceTrackingMaterialIssue & {
  workOrderId?: number | null;
  workOrderNumber?: string | null;
};

function collectMaterialIssues(workOrders: InvoiceTrackingWorkOrder[]): MaterialIssueWithWo[] {
  const out: MaterialIssueWithWo[] = [];
  for (const wo of workOrders) {
    for (const issue of wo.materialIssues ?? []) {
      out.push({
        ...issue,
        workOrderId: wo.workOrderId,
        workOrderNumber: wo.workOrderNumber,
      });
    }
  }
  return out;
}

function materialIssuesForHeat(
  issues: MaterialIssueWithWo[],
  heatNo: string
): MaterialIssueWithWo[] {
  const target = heatNo.trim().toUpperCase();
  return issues.filter((issue) => {
    if (String(issue.heatNumber ?? '').trim().toUpperCase() === target) return true;
    return (issue.heatSources ?? []).some(
      (src) => String(src.heatNo ?? '').trim().toUpperCase() === target
    );
  });
}

function lineDescription(line: InvoiceTrackingLine): string {
  if (line.endgoodName) return line.endgoodName;
  if (line.rawItemName) return line.rawItemName;
  return '—';
}

function formatQty(value?: number | null, uom?: string | null): string {
  if (value == null) return '—';
  const n = Number(value);
  const qty = Number.isFinite(n)
    ? n.toLocaleString('en-IN', { maximumFractionDigits: 3 })
    : String(value);
  return uom ? `${qty} ${uom}` : qty;
}

function formatMoney(value?: number | null): string {
  if (value == null) return '—';
  return Number(value).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  });
}

/** Color badge by heat prefix (e.g. BOP-*, H-*). */
function heatPrefix(heatNo: string): string {
  const trimmed = heatNo.trim().toUpperCase();
  const dash = trimmed.indexOf('-');
  if (dash > 0) return trimmed.slice(0, dash);
  // e.g. H123 without dash — take leading letters
  const match = trimmed.match(/^[A-Z]+/);
  return match?.[0] ?? trimmed;
}

const HEAT_PREFIX_COLORS: Record<string, string> = {
  BOP: 'border-amber-300 bg-amber-50 text-amber-900',
  H: 'border-sky-300 bg-sky-50 text-sky-800',
  RM: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  WO: 'border-violet-300 bg-violet-50 text-violet-800',
};

const HEAT_FALLBACK_COLORS = [
  'border-rose-300 bg-rose-50 text-rose-800',
  'border-cyan-300 bg-cyan-50 text-cyan-800',
  'border-indigo-300 bg-indigo-50 text-indigo-800',
  'border-teal-300 bg-teal-50 text-teal-800',
  'border-orange-300 bg-orange-50 text-orange-900',
  'border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800',
];

function heatBadgeClass(heatNo: string): string {
  const prefix = heatPrefix(heatNo);
  if (HEAT_PREFIX_COLORS[prefix]) return HEAT_PREFIX_COLORS[prefix];
  // Stable color per unknown prefix
  let hash = 0;
  for (let i = 0; i < prefix.length; i++) {
    hash = (hash + prefix.charCodeAt(i) * (i + 1)) % 997;
  }
  return HEAT_FALLBACK_COLORS[hash % HEAT_FALLBACK_COLORS.length];
}

function HeatNumberBadges({
  values,
  onHeatClick,
}: {
  values?: Array<string | null | undefined> | null;
  onHeatClick?: (heatNo: string) => void;
}) {
  const heats = (values ?? []).map((v) => String(v ?? '').trim()).filter(Boolean);
  if (heats.length === 0) {
    return <span className="text-gray-500">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {heats.map((heat, i) => (
        <button
          key={`${heat}-${i}`}
          type="button"
          className="inline-flex"
          title={`View material issue details for ${heat}`}
          onClick={(e) => {
            e.stopPropagation();
            onHeatClick?.(heat);
          }}
        >
          <Badge
            variant="outline"
            className={`font-mono text-xs rounded-md px-2 py-0.5 cursor-pointer hover:opacity-80 ${heatBadgeClass(heat)}`}
          >
            {heat}
          </Badge>
        </button>
      ))}
    </div>
  );
}

export function InvoiceTrackingPage() {
  const { selectedBranch } = useBranch();
  const [searchParams, setSearchParams] = useSearchParams();
  const invoiceNoFromUrl = (searchParams.get('invoiceNo') ?? '').trim();

  const [invoiceNo, setInvoiceNo] = useState(invoiceNoFromUrl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InvoiceTrackingDto | null>(null);
  const [selectedHeatNo, setSelectedHeatNo] = useState<string | null>(null);

  useEffect(() => {
    setInvoiceNo(invoiceNoFromUrl);
  }, [invoiceNoFromUrl]);

  const fetchTracking = useCallback(async (trimmed: string, branchId: number) => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await trackingApiService.getTrackingByInvoiceNumber(trimmed, branchId);
      if (!res.success || !res.data?.found) {
        const message = res.message || res.data?.message || 'Invoice not found';
        setError(message);
        setResult(res.data ?? null);
        return;
      }
      setResult(res.data);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch invoice tracking');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load from URL (search / back / forward / branch change)
  useEffect(() => {
    if (!invoiceNoFromUrl) {
      setResult(null);
      setError(null);
      return;
    }
    if (selectedBranch == null) {
      setError(null);
      setResult(null);
      return;
    }
    void fetchTracking(invoiceNoFromUrl, selectedBranch);
  }, [invoiceNoFromUrl, selectedBranch, fetchTracking]);

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = invoiceNo.trim();
    if (!trimmed) {
      toast.error('Enter an invoice number');
      return;
    }
    if (selectedBranch == null) {
      toast.error('Select a branch from the header');
      return;
    }
    // Push into URL so back/forward restores this search
    if (trimmed === invoiceNoFromUrl) {
      void fetchTracking(trimmed, selectedBranch);
    } else {
      setSearchParams({ invoiceNo: trimmed });
    }
  };

  const invoice = result?.invoice;
  const lines = result?.lines ?? [];
  const workOrders = result?.workOrders ?? [];
  const allMaterialIssues = useMemo(() => collectMaterialIssues(workOrders), [workOrders]);
  const selectedHeatIssues = useMemo(
    () => (selectedHeatNo ? materialIssuesForHeat(allMaterialIssues, selectedHeatNo) : []),
    [allMaterialIssues, selectedHeatNo]
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Invoice Tracking</h1>
          <p className="mt-1 text-gray-600">
            Look up an invoice by number for the selected branch. Expand sections for boxes, work
            orders, material issues, and heat / GRN / PO sources.
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1 w-full space-y-1.5">
                <Label htmlFor="invoiceNo">Invoice number</Label>
                <Input
                  id="invoiceNo"
                  value={invoiceNo}
                  onChange={(e) => setInvoiceNo(e.target.value)}
                  placeholder="e.g. INV/2025/001"
                  disabled={loading}
                />
              </div>
              <Button type="submit" disabled={loading || selectedBranch == null} className="sm:w-auto w-full">
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Searching…
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Track
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {error && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="py-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-amber-900">Not found</p>
                <p className="text-sm text-amber-800">{error}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {result?.found && invoice && (
          <>
            {/* Summary */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="text-xl flex items-center gap-2">
                    <Receipt className="h-5 w-5 text-primary-600" />
                    {invoice.invoiceNo}
                  </CardTitle>
                  <div className="flex flex-wrap gap-2">
                    {invoice.status && (
                      <Badge variant="outline">{invoice.status}</Badge>
                    )}
                    <Badge
                      variant="outline"
                      className={
                        result.hasLinkedBoxes
                          ? 'border-green-300 text-green-800 bg-green-50'
                          : 'border-gray-300 text-gray-600'
                      }
                    >
                      {result.hasLinkedBoxes ? 'Has linked boxes' : 'No linked boxes'}
                    </Badge>
                  </div>
                </div>
                <CardDescription>Invoice summary — expand below for details</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <SummaryField label="Invoice date" value={invoice.invoiceDate ? formatDate(invoice.invoiceDate) : '—'} />
                  <SummaryField label="Branch" value={invoice.branchName || '—'} />
                  <SummaryField label="Supply type" value={invoice.supplyType || '—'} />
                  <SummaryField label="Bill to" value={invoice.billToCustomerName || '—'} />
                  <SummaryField label="Ship to" value={invoice.shipToCustomerName || '—'} />
                  <SummaryField label="Sales order" value={invoice.salesOrderNumber || '—'} />
                  <SummaryField label="Grand total" value={formatMoney(invoice.grandTotal)} />
                  <SummaryField label="IRN" value={invoice.irn || '—'} />
                  <SummaryField label="E-way bill" value={invoice.ewayBillNo || '—'} />
                  <SummaryField label="Line items" value={String(lines.length)} />
                  <SummaryField label="Linked work orders" value={String(workOrders.length)} />
                </div>
              </CardContent>
            </Card>

            <Accordion type="multiple" className="space-y-3" defaultValue={['lines']}>
              {/* Lines */}
              <AccordionItem value="lines" className="border rounded-lg bg-white px-4 shadow-sm">
                <AccordionTrigger className="hover:no-underline">
                  <span className="flex items-center gap-2 font-semibold text-gray-900">
                    <Package className="h-4 w-4 text-primary-600" />
                    Invoice lines ({lines.length})
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  {lines.length === 0 ? (
                    <p className="text-sm text-gray-500">No line items.</p>
                  ) : (
                    <Accordion type="multiple" className="space-y-2">
                      {lines.map((line, idx) => (
                        <LineDetailAccordion
                          key={line.invoiceDetailId ?? idx}
                          line={line}
                          index={idx}
                          onHeatClick={(heatNo) => setSelectedHeatNo(heatNo)}
                        />
                      ))}
                    </Accordion>
                  )}
                </AccordionContent>
              </AccordionItem>

              {/* Work orders */}
              <AccordionItem value="workOrders" className="border rounded-lg bg-white px-4 shadow-sm">
                <AccordionTrigger className="hover:no-underline">
                  <span className="flex items-center gap-2 font-semibold text-gray-900">
                    <Factory className="h-4 w-4 text-purple-600" />
                    Work orders ({workOrders.length})
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  {workOrders.length === 0 ? (
                    <p className="text-sm text-gray-500">No linked work orders.</p>
                  ) : (
                    <Accordion type="multiple" className="space-y-2">
                      {workOrders.map((wo, idx) => (
                        <WorkOrderDetailAccordion
                          key={wo.workOrderId ?? idx}
                          workOrder={wo}
                          onHeatClick={(heatNo) => setSelectedHeatNo(heatNo)}
                        />
                      ))}
                    </Accordion>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </>
        )}

        <HeatMaterialIssueDialog
          heatNo={selectedHeatNo}
          issues={selectedHeatIssues}
          open={selectedHeatNo != null}
          onOpenChange={(open: boolean) => {
            if (!open) setSelectedHeatNo(null);
          }}
        />
      </div>
    </div>
  );
}

function SummaryField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className="font-medium text-gray-900 break-words">{value}</p>
    </div>
  );
}

function WorkOrderLink({
  workOrderId,
  workOrderNumber,
  className,
}: {
  workOrderId?: number | null;
  workOrderNumber?: string | null;
  className?: string;
}) {
  const label = workOrderNumber || (workOrderId != null ? `WO #${workOrderId}` : null);
  if (!label) return <span>—</span>;
  if (workOrderId == null) {
    return <span className={className}>{label}</span>;
  }
  return (
    <Link
      to={`/work-orders/${workOrderId}/view`}
      target="_blank"
      rel="noopener noreferrer"
      className={`text-primary-600 hover:underline font-medium ${className ?? ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </Link>
  );
}

function LineDetailAccordion({
  line,
  index,
  onHeatClick,
}: {
  line: InvoiceTrackingLine;
  index: number;
  onHeatClick: (heatNo: string) => void;
}) {
  const linkedWos = line.linkedWorkOrders ?? [];
  const title = lineDescription(line);
  const boxCount = (line.boxes ?? []).length || linkedWos.reduce((s, wo) => s + (wo.boxCount ?? 0), 0);

  return (
    <AccordionItem value={`line-${line.invoiceDetailId ?? index}`} className="border rounded-md px-3">
      <AccordionTrigger className="hover:no-underline py-3 text-sm">
        <div className="flex flex-wrap items-center gap-2 text-left">
          <span className="font-medium">#{index + 1} {title}</span>
          {line.lineItemType && (
            <Badge variant="outline" className="text-xs">
              {line.lineItemType}
            </Badge>
          )}
          <span className="text-gray-500">{formatQty(line.qty, line.uom)}</span>
          {line.hasLinkedBoxes && (
            <Badge className="bg-green-100 text-green-800 border-0 text-xs">
              {boxCount} box{boxCount === 1 ? '' : 'es'}
            </Badge>
          )}
          {linkedWos.length > 0 && (
            <span className="text-xs text-gray-500">
              {linkedWos.length} WO{linkedWos.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-3 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <SummaryField label="HSN" value={line.hsn || '—'} />
          <SummaryField label="Rate" value={formatMoney(line.rate)} />
        </div>

        {linkedWos.length > 0 ? (
          <div>
            <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Linked work orders</p>
            <div className="overflow-x-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Work order</TableHead>
                    <TableHead>Boxes</TableHead>
                    <TableHead>Heat numbers</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linkedWos.map((wo, i) => (
                    <TableRow key={wo.workOrderId ?? i}>
                      <TableCell>
                        <WorkOrderLink
                          workOrderId={wo.workOrderId}
                          workOrderNumber={wo.workOrderNumber}
                        />
                      </TableCell>
                      <TableCell>{wo.boxCount ?? 0}</TableCell>
                      <TableCell>
                        <HeatNumberBadges
                          values={wo.materialIssueHeatNumbers}
                          onHeatClick={onHeatClick}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No linked work orders for this line.</p>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

function WorkOrderDetailAccordion({
  workOrder,
  onHeatClick,
}: {
  workOrder: InvoiceTrackingWorkOrder;
  onHeatClick: (heatNo: string) => void;
}) {
  const issues = workOrder.materialIssues ?? [];

  return (
    <AccordionItem
      value={`wo-${workOrder.workOrderId ?? workOrder.workOrderNumber ?? 'x'}`}
      className="border rounded-md px-3"
    >
      <AccordionTrigger className="hover:no-underline py-3 text-sm">
        <div className="flex flex-wrap items-center gap-2 text-left">
          <WorkOrderLink
            workOrderId={workOrder.workOrderId}
            workOrderNumber={workOrder.workOrderNumber}
          />
          {workOrder.status && <Badge variant="outline">{workOrder.status}</Badge>}
          {workOrder.endgoodName && (
            <span className="text-gray-600">{workOrder.endgoodName}</span>
          )}
          <span className="text-xs text-gray-500">
            {issues.length} material issue{issues.length === 1 ? '' : 's'}
          </span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-3 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <SummaryField label="Qty" value={formatQty(workOrder.quantity)} />
          <SummaryField label="Completed" value={formatQty(workOrder.completedQuantity)} />
          <SummaryField label="Reject" value={formatQty(workOrder.rejectQuantity)} />
          <SummaryField label="End good" value={workOrder.endgoodName || '—'} />
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Heat number(s)</p>
            <HeatNumberBadges values={workOrder.heatNumbers} onHeatClick={onHeatClick} />
          </div>
        </div>

        {issues.length === 0 ? (
          <p className="text-sm text-gray-500">No material issues for this work order.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase text-gray-500">Material issues</p>
            {issues.map((issue, i) => (
              <MaterialIssueCard key={issue.materialIssueId ?? i} issue={issue} />
            ))}
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

function MaterialIssueCard({
  issue,
  workOrderNumber,
}: {
  issue: MaterialIssueWithWo | InvoiceTrackingMaterialIssue;
  workOrderNumber?: string | null;
}) {
  const woLabel =
    ('workOrderNumber' in issue ? issue.workOrderNumber : null) || workOrderNumber || null;
  const sources = issue.heatSources ?? [];

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        <SummaryField label="Issue ID" value={String(issue.materialIssueId ?? '—')} />
        <SummaryField label="Qty" value={formatQty(issue.issueQuantity)} />
        <SummaryField
          label="Date"
          value={issue.issueDate ? formatDate(issue.issueDate) : '—'}
        />
        <SummaryField label="Heat" value={issue.heatNumber || '—'} />
        <SummaryField label="Status" value={issue.status || '—'} />
        <SummaryField label="Type" value={issue.issueType || '—'} />
        <SummaryField label="Item" value={issue.rawItemName || issue.endgoodName || '—'} />
        {woLabel && <SummaryField label="Work order" value={woLabel} />}
      </div>

      {sources.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase text-gray-500 mb-2">
            Heat / GRN / PO / supplier
          </p>
          <div className="overflow-x-auto border rounded-md bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Heat</TableHead>
                  <TableHead>GRN</TableHead>
                  <TableHead>PO</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Supplier heat</TableHead>
                  <TableHead>Receive date</TableHead>
                  <TableHead>Raw item</TableHead>
                  <TableHead>Received</TableHead>
                  <TableHead>Remaining</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sources.map((src, si) => (
                  <TableRow key={`${src.heatNo}-${src.grnId}-${si}`}>
                    <TableCell className="font-mono text-xs">{src.heatNo || '—'}</TableCell>
                    <TableCell>{src.grnNumber || '—'}</TableCell>
                    <TableCell>{src.poNumber || '—'}</TableCell>
                    <TableCell>{src.supplierName || '—'}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {src.supplierHeatNo || '—'}
                    </TableCell>
                    <TableCell>
                      {src.receiveDate ? formatDate(src.receiveDate) : '—'}
                    </TableCell>
                    <TableCell>{src.rawItemName || '—'}</TableCell>
                    <TableCell>{formatQty(src.receivedQuantity)}</TableCell>
                    <TableCell>{formatQty(src.remainingQuantity)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}

function HeatMaterialIssueDialog({
  heatNo,
  issues,
  open,
  onOpenChange,
}: {
  heatNo: string | null;
  issues: MaterialIssueWithWo[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-6xl lg:max-w-7xl w-full max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            Material issue details
            {heatNo && (
              <Badge
                variant="outline"
                className={`font-mono text-xs ${heatBadgeClass(heatNo)}`}
              >
                {heatNo}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Material issues and GRN / PO / supplier sources for this heat number.
          </DialogDescription>
        </DialogHeader>

        {issues.length === 0 ? (
          <p className="text-sm text-gray-600 py-4">
            No material issue details found for heat {heatNo || '—'}.
          </p>
        ) : (
          <div className="space-y-3">
            {issues.map((issue, i) => (
              <MaterialIssueCard key={issue.materialIssueId ?? i} issue={issue} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default InvoiceTrackingPage;
